import type { AsyncData, AsyncDataOptions, NuxtApp } from '#app'
import { computed, getCurrentInstance, getCurrentScope, isRefOrGetter, nextTick, onScopeDispose, onUnmounted, reactive, toValue, useApi, useAsyncData, useQueryCache, watch, type ApiError, type OpenapiError } from '#imports'
import type { UseQueryFunctionOptions, UseQueryHandler, UseQueryOptions, UseQueryReturn, UseQuerySharedOptions, UseQueryUrl } from '../../types'
import { omit, once } from 'es-toolkit'
import type { Merge } from 'type-fest'

export function useQuery<TResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null>(requestOrHandler: UseQueryUrl, queryOptions: UseQueryOptions<TResponse>): UseQueryReturn<TResponse, TError>
export function useQuery<TResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null>(requestOrHandler: UseQueryHandler<TResponse>, queryOptions: UseQueryFunctionOptions<TResponse>): UseQueryReturn<TResponse, TError>
export function useQuery<TResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null>(requestOrHandler: unknown, queryOptions: unknown): UseQueryReturn<TResponse, TError> {
    const { key: asyncDataKey, enabled: enabledUser = true, dedupe = 'defer', queryCache = {
        ttl: 10,
    }, ...queryOptionsUser } = queryOptions as UseQuerySharedOptions & AsyncDataOptions<TResponse>

    const ASYNC_DATA_OPTION_KEYS = ['deep', 'default', 'getCachedData', 'immediate', 'lazy', 'pick', 'server', 'transform', 'watch'] as const
    const cacheManager = useQueryCache<TResponse>()
    const asyncDataOptions: AsyncDataOptions<TResponse> = {
        ...queryOptionsUser,
        immediate: queryOptionsUser.immediate ?? toValue(enabledUser),
        dedupe,
    }

    if (queryCache) {
        asyncDataOptions.getCachedData = cacheManager.getCachedDataHandler

        asyncDataOptions.transform = async (input) => {
            let preparedInput = null

            if (queryOptionsUser.transform) {
                preparedInput = await queryOptionsUser.transform(input)
            }
            else {
                preparedInput = input
            }

            return cacheManager.transformHandler(toValue(asyncDataKey), preparedInput, queryCache.ttl)
        }
    }

    // Async data handler
    let controller: AbortController | undefined
    const apiOptions: Record<string, any> | undefined = typeof requestOrHandler === 'function' ? undefined : reactive(omit(queryOptionsUser, ASYNC_DATA_OPTION_KEYS))

    const getApi = once(() => useApi({
        enableCookieForwarding: (queryOptionsUser as any).enableCookieForwarding,
        enableLegacySupport: (queryOptionsUser as any).enableLegacySupport,
        service: (queryOptionsUser as any).service,
    }))

    function asyncDataHandler(nuxtApp?: NuxtApp) {
        controller?.abort()
        controller = typeof AbortController !== 'undefined' ? new AbortController() : {} as AbortController

        if (typeof requestOrHandler === 'function') {
            return requestOrHandler({ nuxtApp, signal: controller!.signal })
        }
        else {
            apiOptions!.signal = controller.signal

            return getApi()(toValue(requestOrHandler as UseQueryUrl), toValue(apiOptions))
        }
    }

    const query = useAsyncData<TResponse, TError>(asyncDataKey, asyncDataHandler, asyncDataOptions) as AsyncData<TResponse, TError>

    const isLoading = computed(() => query.status.value === 'idle' || query.status.value === 'pending')

    // Snapshot of query's data properties + isLoading, without then/catch/finally.
    // enrich spreads from this so isLoading is included without putting reactive
    // objects onto the native Promise returned by useAsyncData.
    const queryData = {
        ...omit(query as any, ['then', 'catch', 'finally']),
        isLoading,
        enrich,
        bind,
    }

    /**
     * Extends this query with additional reactive properties, returning a merged
     * object that includes both the query's own properties (data, error, status,
     * refresh, etc.) and the supplied properties.
     *
     * The returned object is a thenable that chains on the original query promise,
     * so `await`-ing it waits for the query to resolve. Properties are also
     * accessible synchronously (no await needed for template refs).
     *
     * Use `enrich` when the composable's consumer needs access to query internals
     * (e.g. `data`, `refresh`, `status`) alongside derived state.
     *
     * **Why this exists:** In Nuxt SSR, reactive effects (computed, watch) created
     * from query state can outlive the request if returned in a plain object.
     * `enrich` chains derived state onto the query's promise, keeping everything
     * in the same lifecycle so Nuxt can track and clean up the async operation.
     * Returning a plain `{ ... }` object instead breaks this chain and causes
     * memory leaks on the server.
     *
     * @example
     * ```ts
     * const useGameData = () => {
     *     const query = useQuery(handler, { key: 'game' })
     *     const formatted = computed(() => query.data.value?.name)
     *     // Returns: { data, error, status, refresh, ..., formatted }
     *     return query.enrich({ formatted })
     * }
     * ```
     */
    function enrich<TProperties extends Record<string, any>>(properties: TProperties): Merge<typeof queryData, TProperties> {
        const plain = {
            ...queryData,
            ...properties,
        }

        return {
            ...plain,
            then(onfulfilled?: any, onrejected?: any) {
                return query.then(() => plain).then(onfulfilled, onrejected)
            },
            catch(onrejected?: any) {
                return query.then(() => plain).catch(onrejected)
            },
            finally(onfinally?: any) {
                return query.then(() => plain).finally(onfinally)
            },
        } as unknown as Merge<typeof queryData, TProperties>
    }

    /**
     * Binds reactive properties to this query's lifecycle **without** including
     * the query's own properties (data, error, status, refresh, etc.) in the
     * return value.
     *
     * Like `enrich`, the returned object is a thenable chained on the query
     * promise — but it only contains the properties you pass in.
     *
     * Use `bind` when a composable creates or wraps a query internally but its
     * public API should not expose query internals. The composable can still
     * read `query.data`, `query.error`, etc. internally via the local variable.
     *
     * **Why this exists:** Same SSR memory-leak concern as `enrich`. Any
     * composable that instantiates a `useQuery` — directly or transitively —
     * must return through `enrich` or `bind` so derived reactive state stays
     * chained to the query's promise lifecycle. `bind` is the lighter option
     * when consumers don't need `data`/`refresh`/`status`.
     *
     * @example
     * ```ts
     * const useMoneyLimits = () => {
     *     const configsQuery = useConfigs()          // wraps useQuery internally
     *     const limits = computed(() => configsQuery.data.value?.limits)
     *     const isValid = computed(() => limits.value?.length > 0)
     *     // Returns: { limits, isValid } — no query data/error/status
     *     return configsQuery.bind({ limits, isValid })
     * }
     * ```
     */
    function bind<TProperties extends Record<string, any>>(properties: TProperties): TProperties & Promise<TProperties> {
        const plain = { ...properties }

        return {
            ...plain,
            then(onfulfilled?: any, onrejected?: any) {
                return query.then(() => plain).then(onfulfilled, onrejected)
            },
            catch(onrejected?: any) {
                return query.then(() => plain).catch(onrejected)
            },
            finally(onfinally?: any) {
                return query.then(() => plain).finally(onfinally)
            },
        } as unknown as TProperties & Promise<TProperties>
    }

    if (import.meta.client) {
        const instance = getCurrentInstance()
        const scope = getCurrentScope()

        if (isRefOrGetter(enabledUser)) {
            const stop = watch(enabledUser, async (enabledUser) => {
                if (!enabledUser) return

                await nextTick()

                // Transitioning enabled→true must not force a cache-bypassing
                // network request when a still-fresh cache entry already exists.
                // refresh() bypasses getCachedData by design, so a freshly-cached
                // key — e.g. one populated by a sibling consumer that mounted
                // moments earlier — would otherwise refetch needlessly. Serve from
                // the cache while it's valid; only hit the network once it expires.
                if (cacheManager.get(toValue(asyncDataKey)) !== undefined) return

                await query.refresh({ dedupe: 'defer' })
            })

            if (instance) {
                onUnmounted(stop)
            }
            else if (scope) {
                onScopeDispose(stop)
            }
        }
    }

    return {
        ...queryData,
        then(onfulfilled?: any, onrejected?: any) {
            return query.then(() => queryData).then(onfulfilled, onrejected)
        },
        catch(onrejected?: any) {
            return query.then(() => queryData).catch(onrejected)
        },
        finally(onfinally?: any) {
            return query.then(() => queryData).finally(onfinally)
        },
    } as unknown as UseQueryReturn<TResponse, TError>
}
